import { captureMessage } from './sentry';

function inViewport(element: Element): boolean {
  const rect = element.getBoundingClientRect();
  return (
    rect.width > 0 &&
    rect.height > 0 &&
    rect.bottom > 0 &&
    rect.right > 0 &&
    rect.top < window.innerHeight &&
    rect.left < window.innerWidth
  );
}

export function photoScreenSnapshot() {
  const screen = document.querySelector('[data-testid^="screen-"]');
  const cards = Array.from(
    document.querySelectorAll<HTMLElement>('[data-photo-image]')
  ).slice(0, 12);
  const gallery = document.querySelector<HTMLElement>(
    '[data-testid="groups-grid"]'
  );
  return {
    screen: screen?.getAttribute('data-testid') ?? null,
    document_visibility: document.visibilityState,
    online: navigator.onLine,
    gallery_scroll_left: gallery?.scrollLeft ?? null,
    gallery_width: gallery?.clientWidth ?? null,
    visible_group_ids: [
      ...new Set(
        cards
          .filter(inViewport)
          .flatMap((card) =>
            card.dataset.groupId ? [card.dataset.groupId] : []
          )
      ),
    ],
    images: cards.map((card) => {
      const image = card.querySelector('img');
      return {
        group_id: card.dataset.groupId,
        image_id: card.dataset.imageId ?? null,
        in_viewport: inViewport(card),
        image_present: image !== null,
        complete: image?.complete ?? false,
        natural_width: image?.naturalWidth ?? 0,
        opacity: image ? getComputedStyle(image).opacity : null,
        image_error:
          card.dataset.imageError === 'true' ||
          card.querySelector('[data-image-error="true"]') !== null,
      };
    }),
  };
}

export function observePhotoRequest(request: Request, timeoutMs: number) {
  if (request.method !== 'GET') return;
  const match = new URL(request.url).pathname.match(
    /\/v1\/entr\/(groups(?:\/([^/]+))?|group-portrait\/([^/]+))$/
  );
  if (!match) return;
  const requestId = crypto.randomUUID();
  const startedAt = performance.now();
  const groupId = match[2] === 'lookup' ? null : (match[2] ?? match[3]);
  let phase = 'request';
  let status: number | null = null;
  let attempt = 1;
  let reported = false;
  const details = () => ({
    diagnostic_request_id: requestId,
    endpoint: match[1],
    group_id: groupId ?? null,
    phase,
    status,
    attempt,
    elapsed_ms: Math.round(performance.now() - startedAt),
    timeout_ms: timeoutMs,
    screen_state: photoScreenSnapshot(),
  });
  const timer = setTimeout(() => {
    reported = true;
    captureMessage('ENTR photo request stalled', 'warning', details());
  }, timeoutMs);
  return {
    receivedHeaders(responseStatus: number): void {
      status = responseStatus;
      phase = 'response-body';
    },
    retry(retryNumber: number): void {
      attempt = retryNumber + 1;
    },
    finish(outcome: 'completed' | 'failed' | 'aborted'): void {
      clearTimeout(timer);
      if (reported) {
        captureMessage('ENTR photo request settled', 'info', {
          ...details(),
          outcome,
        });
      }
    },
  };
}

export function reportPhotoImageFailure(input: {
  group_id: string;
  image_id: string | null;
  photo_type: string;
  attempts: number;
}): void {
  captureMessage('ENTR photo image retries exhausted', 'warning', {
    ...input,
    screen_state: photoScreenSnapshot(),
  });
}

export function reportNewGroupsOutsideViewport(newGroupIds: string[]): void {
  if (newGroupIds.length === 0 || document.hidden) return;
  const snapshot = photoScreenSnapshot();
  if (
    snapshot.visible_group_ids.length > 0 &&
    !newGroupIds.some((id) => snapshot.visible_group_ids.includes(id))
  ) {
    captureMessage('ENTR new groups outside visible panel', 'info', {
      new_group_ids: newGroupIds.slice(0, 12),
      screen_state: snapshot,
    });
  }
}
